import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { FileText, Shield, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsAcceptanceModal = ({ isOpen, onAccepted, onDeclined }) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedGuidelines, setAcceptedGuidelines] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setScrolledToBottom(true);
    }
  };

  const canAccept = acceptedTerms && acceptedPrivacy && acceptedGuidelines;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg max-h-[90vh]" hideClose>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-6 w-6 text-emerald-500" />
            Terms & Conditions
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">
            Please review and accept our Terms of Service, Privacy Policy, and Community Guidelines to continue.
          </p>
          
          <ScrollArea 
            className="h-48 border rounded-lg p-4 bg-gray-50"
            onScroll={handleScroll}
          >
            <div className="text-sm text-gray-700 space-y-4">
              <h4 className="font-semibold">Summary of Key Terms:</h4>
              
              <div>
                <strong>Age Requirement:</strong>
                <p>You must be at least 18 years old to participate in prize-based competitions.</p>
              </div>
              
              <div>
                <strong>Virtual Tokens:</strong>
                <p>Emeralds and Rubies are virtual tokens used within the GEMS Live platform. They are not a currency, stored value, or financial account. Redemptions require identity verification.</p>
              </div>
              
              <div>
                <strong>Skill-Based Competition:</strong>
                <p>GEMS Live hosts skill-based competitive games. Outcomes are determined by player performance, not chance.</p>
              </div>
              
              <div>
                <strong>Fair Play:</strong>
                <p>Cheating, collusion, and exploitation are strictly prohibited and will result in account termination.</p>
              </div>
              
              <div>
                <strong>Content Guidelines:</strong>
                <p>Inappropriate content during streams will result in immediate ban.</p>
              </div>
              
              <div>
                <strong>Privacy:</strong>
                <p>We collect personal information for verification and security purposes. Your data is protected and never sold.</p>
              </div>
              
              <div>
                <strong>Emails from GEMS:</strong>
                <p>By creating an account you agree to receive emails from GEMS — account and security messages, plus news, tips and offers. Every marketing email has a one-click unsubscribe.</p>
              </div>

              <div>
                <strong>Featuring Your Content:</strong>
                <p>You own what you post. You give GEMS permission to feature your posts, photos and live highlights — with your username — to promote GEMS on the platform and on our official channels.</p>
              </div>

              <div>
                <strong>Redemptions:</strong>
                <p>Government-issued ID is required for all redemption requests to prevent fraud.</p>
              </div>
              
              <div className="bg-amber-100 p-3 rounded-lg border border-amber-300">
                <strong className="text-amber-800">⚠️ Important:</strong>
                <p className="text-amber-700">By accepting, you acknowledge that you understand and agree to all terms.</p>
              </div>
            </div>
          </ScrollArea>
          
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-5 w-5 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" className="text-emerald-600 hover:underline font-medium">
                  Terms of Service
                </Link>
              </span>
            </label>
            
            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className="mt-0.5 h-5 w-5 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <Link to="/privacy" target="_blank" className="text-emerald-600 hover:underline font-medium">
                  Privacy Policy
                </Link>
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={acceptedGuidelines}
                onChange={(e) => setAcceptedGuidelines(e.target.checked)}
                className="mt-0.5 h-5 w-5 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <Link to="/community-guidelines" target="_blank" className="text-emerald-600 hover:underline font-medium">
                  Community Guidelines
                </Link>
              </span>
            </label>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onDeclined}
              className="flex-1"
            >
              Decline
            </Button>
            <Button
              onClick={onAccepted}
              disabled={!canAccept}
              className={`flex-1 ${canAccept ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-400'}`}
            >
              <Check className="mr-2 h-4 w-4" />
              I Accept
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 text-center">
            You can review these documents anytime from your profile settings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TermsAcceptanceModal;
